import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import HomePage from './pages/HomePage';
import AdminConsole from './pages/AdminConsole';
import VenueOwnerDashboard from './pages/VenueOwnerDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import GuidePage from './pages/GuidePage';
import ProtectedRoute from './components/ProtectedRoute';
import { hasValidSession } from './utils/auth';
import { MarketProvider, useMarket } from './context/MarketContext';
import { AuthProvider } from './context/AuthContext';
import { isPathMode, getMarketConfig, getMarketConfigBySlug } from './config/markets';
import MarketPickerPage from './pages/MarketPickerPage';
import { trackPageView } from './utils/analytics';

// Injects market theme colors as CSS custom properties on :root
function ThemeInjector({ children }) {
  const market = useMarket();

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--market-primary', market.theme.primaryColor);
    root.style.setProperty('--market-accent', market.theme.accentColor);
    root.style.setProperty('--market-header-bg', market.theme.headerBg);
    root.style.setProperty('--market-header-text', market.theme.headerText);
  }, [market]);

  return children;
}

// Fires a GA4 page_view on every SPA route change
function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);
  return null;
}

/**
 * Shared route definitions used by both env-mode and path-mode.
 * In path-mode, these are nested under /:marketSlug/* so paths are relative.
 */
function MarketRoutes({ publicSiteOpen }) {
  return (
    <Routes>
      {/* Login Route */}
      <Route
        path="/login"
        element={
          !publicSiteOpen && hasValidSession() ? <Navigate to="/" replace /> : <Login />
        }
      />

      {/* Change Password - for first-login password change */}
      <Route
        path="/change-password"
        element={<ChangePassword />}
      />

      {/* Venue Owner Guide - public page */}
      <Route path="/guide" element={<GuidePage />} />

      {/* Home Page - Protected on TCC (shared code), Open on OTC (public site) */}
      <Route
        path="/"
        element={
          publicSiteOpen
            ? <HomePage />
            : <ProtectedRoute><HomePage /></ProtectedRoute>
        }
      />

      {/* Admin Console - Protected (requires admin role) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin" allowedRoles={['admin', 'super_admin']}>
            <AdminConsole />
          </ProtectedRoute>
        }
      />

      {/* Super Admin Dashboard - Cross-market overview */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Venue Owner Dashboard - Purpose-built for venue owners */}
      <Route
        path="/admin/venue"
        element={
          <ProtectedRoute allowedRoles={['venue_owner', 'admin', 'super_admin']}>
            <VenueOwnerDashboard />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home (open markets) or login (gated markets) */}
      <Route path="*" element={<Navigate to={publicSiteOpen ? "/" : "/login"} replace />} />
    </Routes>
  );
}

/**
 * MarketLayout — path-mode wrapper that resolves the market from the URL slug.
 * Mounted at /:marketSlug/* in the outer Router.
 */
function MarketLayout() {
  const { marketSlug } = useParams();
  const config = getMarketConfigBySlug(marketSlug);

  if (!config) {
    return <MarketNotFound slug={marketSlug} />;
  }

  const publicSiteOpen = config.features?.venueOwnerAccounts === true;

  return (
    <MarketProvider config={config}>
      <AuthProvider>
        <ThemeInjector>
          <MarketRoutes publicSiteOpen={publicSiteOpen} />
        </ThemeInjector>
      </AuthProvider>
    </MarketProvider>
  );
}

/**
 * MarketNotFound — shown when the URL slug doesn't match any market.
 */
function MarketNotFound({ slug }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: '#f5f5f5', color: '#333'
    }}>
      <h1 style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>Market Not Found</h1>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        We don't have a market for "{slug}" yet.
      </p>
      <div style={{ display: 'flex', gap: '1rem' }}>
        <a href="/naperville" style={{
          padding: '0.75rem 1.5rem', background: '#264653', color: '#fff',
          borderRadius: '8px', textDecoration: 'none', fontWeight: 600
        }}>Naperville</a>
        <a href="/aurora" style={{
          padding: '0.75rem 1.5rem', background: '#264653', color: '#fff',
          borderRadius: '8px', textDecoration: 'none', fontWeight: 600
        }}>Aurora</a>
      </div>
    </div>
  );
}

// =====================================================================
// App — dual-mode entry point
// =====================================================================

function App() {
  // PATH MODE (OTC): no REACT_APP_MARKET → market comes from URL slug
  if (isPathMode()) {
    return (
      <HelmetProvider>
        <Router>
          <PageTracker />
          <Routes>
            {/* Root → market picker (first visit) or auto-redirect (return visit) */}
            <Route path="/" element={<MarketPickerPage />} />
            {/* Venue Owner Guide - public, top-level (no market slug needed) */}
            <Route path="/guide" element={<GuidePage />} />
            {/* Market-scoped routes */}
            <Route path="/:marketSlug/*" element={<MarketLayout />} />
          </Routes>
        </Router>
      </HelmetProvider>
    );
  }

  // ENV MODE (TCC): REACT_APP_MARKET is set → works exactly as before
  const marketConfig = getMarketConfig();
  const publicSiteOpen = marketConfig.features?.venueOwnerAccounts === true;

  return (
    <HelmetProvider>
      <MarketProvider>
        <AuthProvider>
          <ThemeInjector>
            <Router>
              <PageTracker />
              <MarketRoutes publicSiteOpen={publicSiteOpen} />
            </Router>
          </ThemeInjector>
        </AuthProvider>
      </MarketProvider>
    </HelmetProvider>
  );
}

export default App;
